import { type ReactNode, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'

import type { IIdentityConfirmKyc } from '~/@api/generated-api-types-user/Api'
import { cn } from '~/shadcn/lib/utils'
import { Form } from '~/shadcn/ui/form/form'
import { Card } from '~/shadcn/ui/card'
import { Separator } from '~/shadcn/ui/separator'
import { YesNoRadioGroup } from '~/shadcn/ui/yes-no-radio-group'
import { Spinner } from '~/shadcn/ui/spinner'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { LabelValueList } from '~/@mid_ui/label-value/LabelValueList'
import { ArrowForwardIcon } from '~/@mid_ui/icons/ArrowForwardIcon'
import { CheckCircleIcon } from '~/@mid_ui/icons/CheckCircleIcon'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { useCountryName } from '~/@mid_settings/countries/useCountryName'
import { useUser } from '~/@biz_account/useUser/useUser'
import type { UserWithMeta } from '~/@biz_account/useUser/types'

import { StepHeadline } from '../../_onboarding-stepper/StepHeadline'
import { useIdentityConfirmation } from '../useIdentityConfirmation'
import { Button } from '~/shadcn/ui/button/button'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'

const kycFields = [
  {
    name: 'politika',
    label: 'STE POLITICKY EXPONOVANÁ OSOBA:',
    tooltipText:
      'Politicky exponovaná osoba (PEP) je jednotlivec, ktorý zastáva alebo zastával vysoké politické postavenie, jehož finančné transakcie sú sledované s väčšou pozornosťou na ochranu pred praním peňazí a financovaním terorizmu.',
  },
  {
    name: 'sankcie',
    label: 'STE SANKCIONOVANÁ OSOBA:',
    tooltipText:
      'Sankcionovaná osoba je jednotlivec, organizácia alebo entita, voči ktorej boli uvalené oficiálne sankcie, často zo strany vlády alebo medzinárodných organizácií, v dôsledku ich údajného zapojenia do nelegálnych alebo neetických aktivít.',
  },
] as const

type Props = {
  onVeriffConfirmed?: () => void
}

type FormModel = {
  kyc: { politika: boolean; sankcie: boolean }
}

function IdentityVerificationConfirmation({ onVeriffConfirmed }: Props) {
  const { user } = useUser()
  const editData = getEditData(user)
  const {
    mutation: identityConfirmationMutation,
    isPending: isRequestingIdentityConfirmation,
  } = useIdentityConfirmation()
  const isDesktop = useIsDesktop()
  const form = useForm<FormModel>({
    defaultValues: editData.profile,
  })

  const { getCountryName } = useCountryName()
  const country = getCountryName(user?.profile.address?.state ?? '')

  const onSubmit = useCallback(
    (formData: FormModel) => {
      Analytics.track('user-confirmed-address')

      identityConfirmationMutation(formData, {
        onSuccess: onVeriffConfirmed,
      })
    },
    [identityConfirmationMutation, onVeriffConfirmed]
  )

  return (
    <div className="flex flex-col gap-10 w-full">
      <Card className="bg-[#DAFFC7] border border-gray-300 rounded p-3  pb-4">
        <div className="flex flex-row gap-3">
          <CheckCircleIcon
            fillColor="#121212"
            strokeColor="#FFFFFF"
            className="w-7 h-7"
          />
          <div className="flex flex-col gap-2">
            <p className="font-semibold">
              Overenie vašej identity sa podarilo. Skontrolujte vaše údaje.
            </p>
            <p className="text-sm leading-[21px] font-medium">
              Po potvrdení vaších údajov bude verifikácia dokončená.
            </p>
          </div>
        </div>
      </Card>
      <StepHeadline headingText="Skontrolujte si vaše osobné údaje" />
      <div className="flex flex-col gap-5">
        <h2 className="text-xl font-semibold leading-[30px]">
          Kontaktné údaje
        </h2>
        <LabelValueList
          items={[
            { label: 'Meno:', value: user?.profile.firstname },
            { label: 'Priezvisko:', value: user?.profile.lastname },
          ]}
          itemClassName="flex flex-col lg:flex-row gap-5 lg:gap-1 justify-start"
        />
      </div>

      <Separator className="bg-[#E3E3E3]" />

      <div className="flex flex-col gap-5">
        <h2 className="text-xl font-semibold leading-[30px]">
          Adresa trvalého pobytu
        </h2>
        <div className="flex flex-col gap-5 lg:gap-1">
          <LabelValueList
            items={[
              {
                label: 'Ulica a číslo:',
                value: [
                  user?.profile.address?.street,
                  user?.profile.address?.number,
                ]
                  .filter(Boolean)
                  .join('\xa0'),
              },
              { label: 'Mesto:', value: user?.profile.address?.city },
              { label: 'PSČ:', value: user?.profile.address?.zip },
              {
                label: 'Štát:',
                value: country,
              },
            ]}
            itemClassName="flex flex-col lg:flex-row gap-5 lg:gap-1 justify-start"
          />
        </div>
      </div>

      <Separator className="bg-[#E3E3E3]" />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6">
            <h2 className="text-xl font-semibold leading-[30px]">
              Ďalšie informácie
            </h2>
            <div className="flex flex-col gap-6">
              {kycFields.map(({ label, name, tooltipText }) => (
                <div
                  key={name}
                  className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5">
                  <InfoText
                    width={isDesktop ? '300px' : '100%'}
                    text={
                      <div className="flex items-center gap-2">
                        {label} <TextTooltip text={tooltipText} />
                      </div>
                    }
                  />
                  <Controller
                    rules={{
                      validate: (value) => {
                        if (value === undefined) {
                          return 'not_selected'
                        }
                        return true
                      },
                    }}
                    name={`kyc.${name}`}
                    control={form.control}
                    render={({ field }) => (
                      <YesNoRadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isRequestingIdentityConfirmation}
                        className="flex flex-col lg:flex-row gap-5 lg:gap-auto"
                      />
                    )}
                  />
                </div>
              ))}
            </div>
          </div>

          <Button
            variant="accent"
            size={isDesktop ? '3xl' : 'xl'}
            type="submit"
            className={cn(
              'w-full lg:w-fit mt-8',
              isRequestingIdentityConfirmation &&
                'opacity-50 cursor-not-allowed'
            )}
            disabled={
              !!Object.keys(form.formState.errors).length ||
              !user?.profile.phone ||
              isRequestingIdentityConfirmation
            }>
            Všetky údaje sú správne{' '}
            {isRequestingIdentityConfirmation ? (
              <Spinner />
            ) : (
              <ArrowForwardIcon />
            )}
          </Button>
        </form>
      </Form>

      <p className="font-medium">
        Sú niektoré vaše údaje nesprávne?{' '}
        <Link to="mailto:info@investinslovakia.eu" className="text-accent">
          Napíšte nám.
        </Link>
      </p>
    </div>
  )
}

function InfoText({
  width = '160px',
  text,
}: {
  width?: string
  text: ReactNode
}) {
  return (
    <div
      className="text-[13px] text-[#969696] font-semibold leading-[21px] uppercase"
      style={{ width }}>
      {text}
    </div>
  )
}

function getEditData(user?: UserWithMeta) {
  const editData = {
    profile: {
      phone: user?.profile.phone || '',
      kyc: {} as IIdentityConfirmKyc,
    },
  }

  if (user?.profile.kyc && Object.keys(user?.profile.kyc).length) {
    editData.profile.kyc = user?.profile.kyc
  } else {
    kycFields.forEach((item) => {
      editData.profile.kyc[item.name] = false
    })
    // TODO: toto je asi legacy, ale pre istotu check
    // editData.profile.kyc.pozicka = true
  }

  return editData
}

export { IdentityVerificationConfirmation }
